// src/misc/utils/sheetRowUtils.js

/**
 * Safely convert any value to a trimmed string.
 *
 * - Converts null/undefined to empty string
 * - Replaces non-breaking spaces with regular spaces
 * - Trims leading/trailing whitespace
 *
 * @param {*} v - Any value
 * @returns {string}
 */
export function safeStr(v) {
  return String(v ?? "").replace(/\u00A0/g, " ").trim();
}

/**
 * Safely retrieve a value from a row object using lenient column matching.
 *
 * Matching rules:
 * - Header keys are trimmed
 * - Case-insensitive
 * - Multiple spaces collapsed
 *
 * This allows resilience against spreadsheet header changes like:
 * - "Start Time" vs "Start   Time"
 * - "status" vs "Status"
 *
 * @param {Object} row - A single spreadsheet row object
 * @param {...string} candidates - One or more possible column names
 * @returns {*} The matched field value, or empty string if not found
 */
export function getField(row, ...candidates) {
  if (!row || typeof row !== "object") return "";

  const normalizedMap = new Map();

  for (const key of Object.keys(row)) {
    const normalizedKey = safeStr(key)
      .replace(/\s+/g, " ")
      .toLowerCase();
    normalizedMap.set(normalizedKey, row[key]);
  }

  for (const candidate of candidates) {
    const normalizedCandidate = safeStr(candidate)
      .replace(/\s+/g, " ")
      .toLowerCase();

    if (normalizedMap.has(normalizedCandidate)) {
      return normalizedMap.get(normalizedCandidate);
    }
  }

  return "";
}

/**
 * Normalize an event status field from a spreadsheet row.
 *
 * Supports multiple possible column names to tolerate sheet changes.
 * In your current sheet:
 * - Column A is labeled "Sorry I" (with possible whitespace)
 * - "Status" is also allowed for future compatibility
 *
 * @param {Object} row - A single spreadsheet row object
 * @returns {string} Normalized status string
 */
export function normalizeStatus(row) {
  return safeStr(getField(row, "Status", "Sorry I", "Sorry"));
}
